// Clinical assessment section (step 3) for VisitReportPage.

import { useRef, type JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

const SELECT_BLANK = { value: "", label: "— select —" };
const YN  = [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }];
// DRS VisitCognitiveStatus — carer visit observation (distinct from CS baseline CognitiveStatus)
const COG = [
  SELECT_BLANK,
  { value: "alert_oriented",      label: "Alert & oriented" },
  { value: "mildly_confused",     label: "Mildly confused" },
  { value: "moderately_confused", label: "Mod. confused" },
  { value: "severely_confused",   label: "Severe confusion" },
  { value: "unresponsive",        label: "Unresponsive" },
];
// DRS MoodAffect
const MOOD = [
  SELECT_BLANK,
  { value: "positive",               label: "Positive" },
  { value: "neutral",                label: "Neutral" },
  { value: "mildly_withdrawn",       label: "Mildly withdrawn" },
  { value: "significantly_withdrawn",label: "Significantly withdrawn" },
  { value: "agitated",               label: "Agitated" },
  { value: "distressed",             label: "Distressed" },
];
// DRS AppetiteLevel
const APP = [
  SELECT_BLANK,
  { value: "good",               label: "Good" },
  { value: "fair",               label: "Fair" },
  { value: "poor_reduced_intake",label: "Poor / reduced intake" },
  { value: "refused_food",       label: "Refused food" },
  { value: "unable_to_assess",   label: "Unable to assess" },
];
// DRS OverallPatientStatus
const OPS = [
  SELECT_BLANK,
  { value: "stable_as_expected",               label: "Stable as expected" },
  { value: "minor_concerns_monitor",           label: "Minor concerns — monitor" },
  { value: "noticeable_change_cs_review",      label: "Noticeable change — CS review" },
  { value: "significant_concern_gp_recommended",label: "Significant concern — GP recommended" },
  { value: "urgent_immediate_action",          label: "Urgent — immediate action" },
];
const ENGAGEMENT = [
  SELECT_BLANK,
  { value: "fully_engaged",           label: "Fully engaged" },
  { value: "cooperative_but_limited", label: "Cooperative but limited" },
  { value: "minimally_engaged",       label: "Minimally engaged" },
  { value: "refused_all_assessments", label: "Refused all assessments" },
];

const CHANGE = [
  { value: "same", label: "Same as last visit" },
  { value: "improved", label: "Improved" },
  { value: "worsened", label: "Worsened" },
];

// DRS B3 — Functional Status
const AMBUL = [
  SELECT_BLANK,
  { value: "independent_walking", label: "Independent walking" },
  { value: "walks_with_stick",    label: "Walks with stick" },
  { value: "walks_with_walker",   label: "Walks with walker" },
  { value: "wheelchair",          label: "Wheelchair" },
  { value: "non_ambulatory",      label: "Non-ambulatory" },
];
const CONTINENCE = [
  SELECT_BLANK,
  { value: "continent",              label: "Continent" },
  { value: "occasional_leakage",     label: "Occasional leakage" },
  { value: "frequent_incontinence",  label: "Frequent incontinence" },
  { value: "catheterised",           label: "Catheterised" },
];
const GROOMING = [
  SELECT_BLANK,
  { value: "self_grooming",  label: "Self-grooming" },
  { value: "needs_help",     label: "Needs help" },
  { value: "fully_dependent",label: "Fully dependent" },
];
const MED_ADMIN = [
  SELECT_BLANK,
  { value: "self_admin",         label: "Self-administers" },
  { value: "needs_reminder",     label: "Needs reminder" },
  { value: "needs_supervision",  label: "Needs supervision" },
  { value: "fully_dependent",    label: "Fully dependent" },
];
const COG_TEST = [
  { value: "not_done",        label: "Not done" },
  { value: "mmse",            label: "MMSE (max 30)" },
  { value: "moca",            label: "MoCA (max 30)" },
  { value: "mini_cog",        label: "Mini-Cog (max 5)" },
  { value: "clock_drawing",   label: "Clock drawing" },
  { value: "informal_assessment", label: "Informal assessment" },
  { value: "unable_to_assess",label: "Unable to assess" },
];
const COG_INTERP = [
  SELECT_BLANK,
  { value: "normal",          label: "Normal" },
  { value: "mild_decline",    label: "Mild decline" },
  { value: "moderate_decline",label: "Moderate decline" },
  { value: "severe_decline",  label: "Severe decline" },
];

interface ClinicalProps {
  f: FormState; dispatch: (a: Action) => void;
  bus?: SectionAutoSaveBus; fieldErrors?: SectionFieldErrors;
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function ClinicalSection({ f, dispatch, bus: _bus, fieldErrors, focusFieldName }: ClinicalProps): JSX.Element {
  // bus reserved for free-text fields; Clinical has no textareas today.
  const fn = (field: Action["field"]) => (v: string) => dispatch({ field, value: v } as Action);
  const err = (api: string): string | undefined => fieldErrorMessage(fieldErrors, api);
  const cogTestActive = f.cog_test_used !== "" && f.cog_test_used !== "not_done" && f.cog_test_used !== "unable_to_assess";
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);
  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>3 — Clinical Assessment</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Pain present?" value={f.pain_present} onChange={fn("pain_present")} options={YN} error={err("pain_present")} />
        {f.pain_present === "yes" && <SelectField label="Pain vs. last visit" value={f.pain_vs_last} onChange={fn("pain_vs_last")} options={CHANGE} error={err("pain_vs_last_visit")} />}
        {f.pain_present === "yes" && <FormField label="Pain severity (0–10)" type="number" value={f.pain_severity} onChange={fn("pain_severity")} error={err("pain_severity")} />}
        {f.pain_present === "yes" && <FormField label="Pain location" value={f.pain_location} onChange={fn("pain_location")} error={err("pain_location")} />}
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-4 mb-2">Functional Status</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Ambulation status" value={f.ambulation} onChange={fn("ambulation")} options={AMBUL} error={err("ambulation_status")} />
        <FormField label="ADL score (0=fully dependent, 6=independent)" type="number" value={f.adl_score} onChange={fn("adl_score")} min={0} max={6} error={err("adl_score")} />
        <SelectField label="Continence (this visit)" value={f.continence} onChange={fn("continence")} options={CONTINENCE} error={err("continence_status_visit")} />
        <SelectField label="Grooming / hygiene" value={f.grooming} onChange={fn("grooming")} options={GROOMING} error={err("grooming_status")} />
        <SelectField label="Medication self-administration capacity" value={f.med_admin_cap} onChange={fn("med_admin_cap")} options={MED_ADMIN} error={err("medication_admin_capacity")} />
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-4 mb-2">Cognitive Assessment</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Cognitive status" value={f.cognitive} onChange={fn("cognitive")} options={COG} error={err("cognitive_status")} />
        <SelectField label="Cognition vs. last visit" value={f.cognitive_vs_last} onChange={fn("cognitive_vs_last")} options={CHANGE} error={err("cognitive_vs_last_visit")} />
        <SelectField label="Cognitive test used (if any)" value={f.cog_test_used} onChange={fn("cog_test_used")} options={COG_TEST} error={err("cognitive_test_used")} />
        {cogTestActive && <FormField label="Test score" type="number" value={f.cog_test_score} onChange={fn("cog_test_score")} error={err("cognitive_test_score")} />}
        {cogTestActive && <SelectField label="Cognitive assessment interpretation" value={f.cog_interpretation} onChange={fn("cog_interpretation")} options={COG_INTERP} error={err("cognitive_assessment_interpretation")} />}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <SelectField label="Mood / affect" value={f.mood} onChange={fn("mood")} options={MOOD} error={err("mood_affect")} />
        <SelectField label="Appetite today" value={f.appetite} onChange={fn("appetite")} options={APP} error={err("appetite_today")} />
        <SelectField label="Overall patient status" value={f.overall_status} onChange={fn("overall_status")} options={OPS} error={err("overall_patient_status")} apiFieldName="overall_patient_status" required />
        <SelectField label="Patient engagement level" value={f.patient_engagement_level}
          onChange={fn("patient_engagement_level")} options={ENGAGEMENT} error={err("patient_engagement_level")} apiFieldName="patient_engagement_level" required />
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={f.escalation}
          onChange={(e) => dispatch({ field: "escalation", value: e.target.checked })}
          className="rounded border-slate-300 text-navy-700 focus:ring-navy-500" />
        Flag for urgent review
      </label>
    </div>
  );
}
